<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.tags', 2),
                        href: null,
                    },
                    {
                        name: $tc('messages.attempts', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <EmptyState v-if="subjects.length === 0" picture/>
        <div v-else class="flex flex-wrap gap-x-2">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id"/>
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter" v-if="filters.subject_id">
            <div class="flex justify-end mt-5">
                <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                <div class="mt-1">
                    <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" is-filter/>
                </div>
            </div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1" v-if="options.contentBlocks.length">
                        <FormLabel :value="$tc('messages.content_blocks', 1)" for="content_block_id"/>
                        <FormSelect
                            id="content_block_id"
                            v-model="form.content_block_id"
                            :options="options.contentBlocks"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.content_block_id" class="mt-2"/>
                    </div>
                </div>
                <div class="sm:col-span-2" v-if="options.gradeLevels.length">
                    <div class="mt-1" >
                        <FormLabel :value="$tc('messages.grade_levels', 1)" for="grade_level_id"/>
                        <FormSelect
                            id="grade_level_id"
                            v-model="form.grade_level_id"
                            :options="options.gradeLevels"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.grade_level_id" class="mt-2"/>
                    </div>
                </div>
                <div class="sm:col-span-2" v-if="options.groups.length">
                    <div class="mt-1" >
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id"/>
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.content_block_id" class="mt-2"/>
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.student')" for="student_id"/>
                        <FormSelect
                            id="subject_id"
                            v-model="form.student_id"
                            :options="options.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2"/>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.activity_mode')" for="mode"/>
                        <FormSelect
                            id="mode"
                            v-model="form.mode"
                            :options="options.modes"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.mode" class="mt-2"/>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.levels', 1)" for="level_reached"/>
                        <FormSelect
                            id="mode"
                            v-model="form.level_reached"
                            :options="options.level_reached"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.level_attempted')"
                        />
                        <FormError :value="form.errors.mode" class="mt-2"/>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from">
                            <ContextMenu>
                                <ul>
                                    <li v-for="period in options.date_periods" :key="period.url" class="mt-1">
                                        <AppButton element="InertiaLink" class="block" :href="period.url" size="sm">
                                            {{ period.text }}
                                        </AppButton>
                                    </li>
                                </ul>
                            </ContextMenu>
                        </FormLabel>
                        <FormInput
                            id="date_from"
                            v-model="form.date_from"
                            type="date"
                            name="date_from"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.date_from" class="mt-2"/>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to"/>
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1 w-full"/>
                        <FormError :value="form.errors.date_to" class="mt-2"/>
                    </div>
                </div>
            </div>
        </form>

        <div v-if="graph">
            <div>
                <apexchart
                    :type="graph.chart.type"
                    :height="graph.chart.height"
                    :series="graph.series"
                    :options="graph"
                />
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import { IconCircleCheckFilled, IconMinus } from '@tabler/icons-vue';
import EmptyState from '@/components/EmptyState.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import ContextMenu from '@/components/ContextMenu.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        IconMinus,
        PlusButton,
        ContextMenu,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
        IconCircleCheckFilled,
        ActivityMenuOptions,
        PanelPage,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'graph', 'graph2', 'group', 'filters', 'options', 'meta'],
    data() {
        return {
            form: this.$inertia.form(this.filters),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index, {
                preserveScroll: true,
            });
        },
    },
};
</script>
